import type { OHLCVData, IndicatorConfig } from '../../types'

interface ChartLegendProps {
  symbol: string
  crosshairData: {
    time: number
    price: number
    ohlcv?: OHLCVData
  } | null
  indicators: IndicatorConfig[]
}

export function ChartLegend({ symbol, crosshairData, indicators }: ChartLegendProps) {
  const formatPrice = (price: number) => {
    if (price > 1000) return price.toFixed(2)
    if (price > 1) return price.toFixed(4)
    return price.toFixed(8)
  }

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000)
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const ohlcv = crosshairData?.ohlcv
  const priceChange = ohlcv ? ((ohlcv.close - ohlcv.open) / ohlcv.open * 100) : 0
  const isPositive = priceChange >= 0

  return (
    <div className="absolute top-4 left-4 z-10 bg-kit-darker/80 backdrop-blur-sm rounded-lg p-3 border border-kit-border">
      {/* Symbol */}
      <div className="flex items-center gap-3 mb-2">
        <span className="text-lg font-bold text-white">{symbol}</span>
        {crosshairData && (
          <span className="text-sm text-gray-400">
            {formatTime(crosshairData.time)}
          </span>
        )}
      </div>

      {/* OHLCV Data */}
      {ohlcv && (
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
          <div className="flex items-center gap-1">
            <span className="text-gray-500">O:</span>
            <span className="text-white font-mono">{formatPrice(ohlcv.open)}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">H:</span>
            <span className="text-kit-green font-mono">{formatPrice(ohlcv.high)}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">L:</span>
            <span className="text-kit-red font-mono">{formatPrice(ohlcv.low)}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-gray-500">C:</span>
            <span className={`font-mono ${isPositive ? 'text-kit-green' : 'text-kit-red'}`}>
              {formatPrice(ohlcv.close)}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className={`font-mono ${isPositive ? 'text-kit-green' : 'text-kit-red'}`}>
              {isPositive ? '+' : ''}{priceChange.toFixed(2)}%
            </span>
          </div>
        </div>
      )}

      {/* Indicators */}
      {indicators.length > 0 && (
        <div className="mt-2 pt-2 border-t border-kit-border">
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs">
            {indicators.filter(i => i.visible).map((indicator) => (
              <div key={indicator.id} className="flex items-center gap-1">
                <div 
                  className="w-2 h-2 rounded-full" 
                  style={{ backgroundColor: indicator.color }}
                />
                <span className="text-gray-400">
                  {indicator.type}
                  {indicator.params.period && `(${indicator.params.period})`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
